import { Box } from 'grommet'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'

type Props = {
  tabs: TabItem[]
  activeTab: string
  setActiveTab: (arg: string) => void
  background?: string
}

export function Tab2({ tabs, activeTab, setActiveTab, background }: Props) {
  return (
    <Box
      background={background || '#CED8FF'}
      border={{ color: '#E5E7EB', size: '1px' }}
      className="max-w-full overflow-x-auto"
      direction="row"
      flex={false}
      gap="2px"
      round="100px"
      style={{
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}
    >
      {tabs?.map(tab => {
        const value = typeof tab === 'string' ? tab : tab.value
        const label = typeof tab === 'string' ? tab : tab.label
        const disabled = typeof tab === 'string' ? false : Boolean(tab.disabled)

        const isActive = !disabled && activeTab.toLowerCase() === value.toLowerCase()

        return (
          <Box
            background={isActive ? '#EDF1F8' : 'transparent'}
            className={`tab shrink-0 ${isActive ? 'active' : ''} ${disabled ? 'opacity-50' : ''}`}
            flex={false}
            key={value}
            margin="2px"
            onClick={disabled ? undefined : () => setActiveTab(value)}
            pad={{ horizontal: '14px', vertical: '8px' }}
            round="100px"
            style={{
              cursor: disabled ? 'not-allowed' : 'pointer',
              pointerEvents: disabled ? 'none' : undefined,
              whiteSpace: 'nowrap'
            }}
          >
            {label}
          </Box>
        )
      })}
    </Box>
  )
}

export type TabItem =
  | string
  | {
      value: string
      label: string
      count?: number
      disabled?: boolean
    }

type Props2 = {
  tabs: TabItem[]
  activeTab: string
  setActiveTab: (arg: string) => void
}

export function Tab3({ activeTab, setActiveTab, tabs }: Props2) {
  return (
    <div className="scrollbar-none -mb-px flex max-w-full gap-x-3 overflow-x-auto border-b border-[#E5E7EB] sm:gap-x-4 md:gap-x-6">
      {tabs?.map(tab => {
        const value = typeof tab === 'string' ? tab : tab.value
        const label = typeof tab === 'string' ? tab : tab.label
        const count = typeof tab === 'string' ? undefined : tab.count

        const isActive = activeTab === value

        return (
          <button
            className={`-mb-px flex shrink-0 items-center gap-x-2 border-b-2 pb-2.5 transition-colors duration-200 focus:outline-none ${
              isActive ? 'border-[#3A5A40] text-[#111827]' : 'border-transparent text-[#6B7280] hover:text-[#374151]'
            }`}
            key={value}
            onClick={() => setActiveTab(value)}
            type="button"
          >
            <span className={`whitespace-nowrap text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{label}</span>

            {typeof count === 'number' && (
              <span
                className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
                  isActive ? 'bg-[#E5E7EB] text-[#111827]' : 'bg-[#F3F4F6] text-[#6B7280]'
                }`}
              >
                {count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export type PatientTabItem = {
  value: string
  label: string
  icon?: ReactNode
}

type PatientTabProps = {
  tabs: PatientTabItem[]
  activeTab: string
  setActiveTab: (value: string) => void
  className?: string
}

export function PatientTab({ tabs, activeTab, setActiveTab, className = '' }: PatientTabProps) {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const [indicator, setIndicator] = useState({ left: 0, width: 0 })
  const [indicatorReady, setIndicatorReady] = useState(false)

  const normalizedActiveTab = useMemo(() => activeTab.toLowerCase(), [activeTab])

  useLayoutEffect(() => {
    const activeTabRef = tabs.find(tab => tab.value.toLowerCase() === normalizedActiveTab)?.value
    const element = activeTabRef ? tabRefs.current[activeTabRef] : null

    if (element) {
      setIndicator({
        left: element.offsetLeft,
        width: element.offsetWidth
      })
      setIndicatorReady(true)
    }
  }, [tabs, normalizedActiveTab])

  return (
    <div
      className={`scrollbar-none relative flex w-full max-w-full shrink-0 items-center gap-x-2 overflow-x-auto border-b border-[#E5E7EB] ${className}`}
    >
      {tabs.map(tab => {
        const isActive = normalizedActiveTab === tab.value.toLowerCase()

        return (
          <button
            className={`relative flex shrink-0 items-center gap-2 px-1 py-2 text-sm text-[#6B7280] transition-colors duration-200 hover:text-[#374151] ${
              isActive ? '!text-[#111827]' : ''
            }`}
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            ref={el => {
              tabRefs.current[tab.value] = el
            }}
            type="button"
          >
            {tab.icon ? <span className={`${isActive ? 'text-[#111827]' : 'text-[#6B7280]'}`}>{tab.icon}</span> : null}
            <span className={`font-medium transition-all duration-200 ${isActive ? 'font-semibold' : 'font-medium'}`}>
              {tab.label}
            </span>
          </button>
        )
      })}

      <span
        className={`pointer-events-none absolute bottom-0 h-0.5 rounded-full bg-[#2DD4BF] ease-out ${
          indicatorReady ? 'transition-all duration-300' : ''
        }`}
        style={{
          left: `${indicator.left}px`,
          opacity: indicatorReady ? 1 : 0,
          width: `${indicator.width}px`
        }}
      />
    </div>
  )
}
